import React, { Suspense, useEffect } from "react";

import store from "./appStore";

import { Hub } from "aws-amplify/utils";
import { DataStore } from "aws-amplify/datastore";

import { getCurrentUser } from "aws-amplify/auth";
import * as Models from "./models";

import { Canvas } from "@react-three/fiber";
import { Preload, useDetectGPU } from "@react-three/drei";

import { EbbnenContext } from "@solidwhite/ebbnen";
import { UIPortalContext } from "@solidwhite/ui-portal";

import { useTranslation } from "react-i18next";
import useHashLocation, { currentLocation } from "./use-hash-location";
import LangRouteChanger from "./components/LangRouteChanger";
import { useSnapshot } from "valtio";
import ReactGA from "react-ga";

import LoadingCallback from "./components/LoadingCallback";
import CameraWrapper from "./components/3d/CameraWrapper";
import Background from "./components/3d/Background";
import PostEffects from "./components/3d/PostEffects";
import InductionFloor from "./components/3d/InductionFloor";
import Overview from "./components/3d/Overview";
import ViewBlocker from "./components/3d/ViewBlocker";
import TexkeysDebugToggle from "./components/ui/HeaderBar/TexkeysDebugToggle";
import CamNearFarSetter from "./components/3d/CamNearFarSetter";

import HeaderBar from "./components/ui/HeaderBar/HeaderBar";
import LoadingScreen from "./components/ui/LoadingScreen";
import Impressum from "./components/ui/Impressum";
import CallToAction from "./components/ui/CallToAction";
import Tutorial, { TutorialSwitch } from "./components/ui/Tutorial/Tutorial";
import AuthListener from "./components/AuthListener";

//import studio from "@theatre/studio";
import { getProject } from "@theatre/core";
import projectState from "./data/theatreState.json";

import { listLanguages, listTranslationKeys, listTranslations } from "./graphql/queries";
import { getLanguageDataGraphQl } from "./utils/getLanguageDataGraphQl";
import { useMultiuserContext } from "@solidwhite/multiuser";
import Synchronizers from "./components/multiuser/Synchronizers";

import MultiuserDebug from "./components/debug/MultiuserDebug";
import SpectatorJoinMenu from "./components/multiuser/ui/SpectatorJoinMenu";
import UserList from "./components/multiuser/ui/UserList";
import useStoreLocation from "./use-store-location";
import SessionEndScreen from "./components/multiuser/ui/SessionEndScreen";
import { TimeoutReset } from "./components/TimeoutReset.tsx";
import { AutoplayProgress } from "./components/Autoplay.tsx";
import PresentatorMenu from "./components/multiuser/ui/PresentatorMenu";
import VideoPlayerPortal from "@ui/SideBar/VideoPlayerPortal.jsx";
import UpdatePanel from "./components/update/UpdatePanel.tsx";
import MediaUpdatePanel from "./components/update/MediaUpdatePanel.tsx";
import QuitButton from "./components/ui/QuitButton";

const TRACKING_ID = "UA-7922348-3"; // OUR_TRACKING_ID
ReactGA.initialize(TRACKING_ID);

// Amplify.Logger.LOG_LEVEL = "DEBUG";

if (process.env.NODE_ENV === "development") {
  //studio.initialize();
  getProject("EFD", { state: projectState });
}

const startTime = Date.now();

function App() {
  const { t, i18n } = useTranslation();
  const [location, setLocation] = useHashLocation();
  const { datastoreReady } = useSnapshot(store);
  const GPUTier = useDetectGPU();
  const performanceMode = GPUTier < 2 || GPUTier.isMobile;

  useEffect(() => {
    ReactGA.pageview(location + window.location.search);
  }, [location]);

  const fillLanguages = async () => {
    const languages = await DataStore.query(Models.Language);
    const translation = await DataStore.query(Models.Translation);
    const translationkeys = await DataStore.query(Models.TranslationKey);

    const language_id_map = new Map(languages.map((l) => [l.id, l]));
    const translationkey_id_map = new Map(translationkeys.map((t) => [t.id, t]));

    let dictionary = {};
    languages.forEach((l) => (dictionary[l.locale] = {}));

    translation
      .filter((t) => translationkey_id_map.has(t.translationKeyID))
      .filter((t) => language_id_map.has(t.languageID))
      .forEach((t) => {
        const locale = language_id_map.get(t.languageID).locale;
        const key = translationkey_id_map.get(t.translationKeyID).key;
        dictionary[locale][key] = t.value;
      });

    const pubishedLangs = languages.filter((l) => l.published);

    if (pubishedLangs.length == 0) {
      console.error("No Language Data found. Make sure there are pubished Lanuages in the CMS.");
      return;
    }

    pubishedLangs.forEach((l) => {
      i18n.addResourceBundle(l.locale, "translations", dictionary[l.locale], true, true);
    });

    store.languages = pubishedLangs;

    store.datastoreReady = true;
  };

  useEffect(() => {
    const removeListener = Hub.listen("datastore", async (capsule) => {
      const {
        payload: { event, data },
      } = capsule;

      if (process.env.NODE_ENV === "development") {
        console.log(event);
      }

      if (event === "ready") {
        fillLanguages();
      }
    });

    // start anyway
    if (import.meta.env.MODE === "electron") {
      fillLanguages();
    }

    DataStore.start();

    return removeListener;
  }, []);

  return (
    <>
      <UIPortalContext>
        <Canvas
          camera={{
            position: [0, 10, 7],
            fov: 60,
            near: 0.03,
            far: 500,
          }}
          gl={{ antialias: false, alpha: true }}
          onCreated={({ camera, raycaster, gl }) => {
            camera.layers.enableAll();
            raycaster.layers.set(1);
            gl.setClearColor(0x000000, 0);
            gl.autoClear = false;
            // gl.info.autoReset = false;
          }}
          id="canvas"
        >
          <Suspense fallback={<LoadingCallback />}>
            <Overview />

            <Background />
            <PostEffects performanceMode={performanceMode} />

            <InductionFloor />
            <ViewBlocker />
            {/* <Preload /> */}
          </Suspense>

          {/* <CameraWrapper /> */}
          <CamNearFarSetter />
          <Synchronizers />
          <TimeoutReset />
        </Canvas>
        {datastoreReady && (
          <>
            {import.meta.env.MODE !== "electron" && <TutorialSwitch />}
            <HeaderBar />
            {import.meta.env.MODE !== "electron" && <Impressum />}
            <CallToAction />
            <LangRouteChanger />
            <AutoplayProgress />
            <VideoPlayerPortal />
          </>
        )}

        <TexkeysDebugToggle />

        <LoadingScreen />
        {import.meta.env.MODE === "electron" && <MediaUpdatePanel />}
        {import.meta.env.MODE === "electron" && <QuitButton />}
        {import.meta.env.MODE === "electron" && <UpdatePanel />}
        <AuthListener />

        <PresentatorMenu />
        <UserList />
        <SpectatorJoinMenu />
      </UIPortalContext>
    </>
  );
}

const EbbnenWrapperDefault = ({ children }) => {
  return <EbbnenContext locationHook={useHashLocation}>{children}</EbbnenContext>;
};

const EbbnenWrapperStoreLocation = ({ children }) => {
  return <EbbnenContext locationHook={useStoreLocation}>{children}</EbbnenContext>;
};

export const EbbnenWrapper = ({ children }) => {
  const { connected, spectating } = useMultiuserContext();

  if (!connected && spectating) return <SessionEndScreen />;

  if (connected && spectating)
    return (
      <EbbnenWrapperStoreLocation>
        <App />
      </EbbnenWrapperStoreLocation>
    );
  else {
    return (
      <EbbnenWrapperDefault>
        <App />
      </EbbnenWrapperDefault>
    );
  }
};

export default App;
